import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { faqCategories, faqs } from '@/mocks/faqData';

export default function FAQ() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('general');
  const [openId, setOpenId] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', question: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const toggleFaq = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  const filteredFaqs = useMemo(() => {
    let result = faqs;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q));
    } else if (activeCategory) {
      result = result.filter((f) => f.category === activeCategory);
    }
    return result;
  }, [search, activeCategory]);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = formData.get('company_alt') as string;
    if (hp && hp.trim() !== '') {
      setFormSubmitted(true);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9r05qee3oq4jqumts30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        setFormError('');
        setContactForm({ name: '', email: '', question: '' });
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">
        {/* Header */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-20 bg-background-100">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Help Center</span>
          <h1 className="text-4xl md:text-6xl font-heading font-bold text-foreground-950 mb-4">Resource Center</h1>
          <p className="text-foreground-600 max-w-2xl text-sm md:text-base leading-relaxed">
            Find answers to common questions about our services, processes, and how we help East African businesses connect with Chinese suppliers.
          </p>
        </div>

        <div className="w-full px-6 md:px-10 lg:px-16">
          {/* Search */}
          <div className="py-8 md:py-10">
            <div className="relative max-w-xl mx-auto">
              <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); if (e.target.value) setActiveCategory(''); }}
                placeholder="Search the knowledge base..."
                className="w-full pl-12 pr-5 py-3.5 text-sm bg-background-50 border border-background-200 rounded-full focus:outline-none focus:border-accent-400 transition-colors text-foreground-800"
              />
            </div>
          </div>

          {/* Category Tabs */}
          {!search && (
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {faqCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.id); setOpenId(null); }}
                  className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-accent-500 text-primary-900 shadow-sm'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {/* FAQ Accordion */}
          <div className="max-w-3xl mx-auto pb-10 md:pb-14">
            {search && (
              <p className="text-sm text-foreground-500 mb-6 text-center">
                {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} for &ldquo;{search}&rdquo;
              </p>
            )}
            {!search && activeCategory && (
              <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-6 text-center">
                {faqCategories.find((c) => c.id === activeCategory)?.name} Questions
              </h2>
            )}

            {filteredFaqs.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                  <i className="ri-question-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-700 mb-2">No results found</h3>
                <p className="text-sm text-foreground-500">Try different keywords or browse by category above.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFaqs.map((faq) => (
                  <div
                    key={faq.id}
                    className={`bg-background-50 border rounded-2xl overflow-hidden transition-all duration-300 ${
                      openId === faq.id ? 'border-accent-200/70 shadow-sm' : 'border-background-200/70'
                    }`}
                  >
                    <button
                      onClick={() => toggleFaq(faq.id)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    >
                      <span className={`text-sm font-medium transition-colors duration-200 ${openId === faq.id ? 'text-primary-700' : 'text-foreground-800'}`}>
                        {faq.question}
                      </span>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${openId === faq.id ? 'bg-accent-500 text-primary-900 rotate-45' : 'bg-background-100 text-foreground-400'}`}>
                        <i className="ri-add-line text-sm"></i>
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-400 ${
                        openId === faq.id ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="px-6 pb-5">
                        <div className="w-full h-px bg-background-200 mb-5"></div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6 text-center">Quick Resource Links</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Product Catalogue', href: '/products', icon: 'ri-shopping-bag-3-line' },
                { label: 'Service Overview', href: '/trading', icon: 'ri-swap-box-line' },
                { label: 'Industry Sectors', href: '/industries', icon: 'ri-building-2-line' },
                { label: 'Get in Touch', href: '/contact', icon: 'ri-mail-line' },
              ].map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-xl hover:border-accent-200/50 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-white transition-all duration-300">
                    <i className={`${link.icon} text-sm`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors">{link.label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Still Have Questions */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">Still Have Questions?</h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                {`Can't find what you're looking for? Send us your question and our team will respond within 24 hours.`}
              </p>
            </div>

            {formSubmitted ? (
              <div className="bg-background-100 border border-background-200 rounded-2xl p-10 text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-2">Question Submitted!</h3>
                <p className="text-sm text-foreground-500">Our team will review your question and respond via email within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} data-readdy-form="true" className="bg-background-100 border border-background-200/70 rounded-2xl p-8 max-w-lg mx-auto">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors text-foreground-800"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors text-foreground-800"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">Your Question *</label>
                    <textarea
                      name="question"
                      required
                      maxLength={500}
                      rows={3}
                      value={contactForm.question}
                      onChange={(e) => setContactForm((f) => ({ ...f, question: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors resize-none text-foreground-800"
                      placeholder="Type your question here..."
                    ></textarea>
                  </div>
                  <div className="hp-field">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  {formError && <div className="text-xs text-red-500 bg-red-50 px-4 py-2.5 rounded-lg">{formError}</div>}
                  <button
                    type="submit"
                    className="w-full px-6 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Submit Question <i className="ri-send-plane-line ml-2"></i>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}